import { Injectable, signal, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { API_CONFIG } from '../config/api.config';

export interface User {
  userid: string;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string | null;
  role?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data?: {
    user: {
      profile: {
        userid: string;
        username: string;
        firstname: string;
        lastname: string;
        email: string;
        phone: string | null;
        createdAt: string;
        updatedAt: string;
      };
      role: {
        role: string;
      };
      facturacion?: {
        nombre: string;
        apellido: string;
        direccion: string;
        telefono: string;
        email: string;
        identificacion: string;
        tipo_identificacion: {
          codigo: string;
          descripcion: string;
        };
      };
    };
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
    refreshExpiresIn: string;
  };
  timestamp: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  turnstileToken?: string;
}

export interface CheckEmailRequest {
  email: string;
  turnstileToken?: string;
}

export interface CheckEmailResponse {
  success: boolean;
  message: string;
  data?: {
    exists: boolean;
    email: string;
  };
  timestamp: string;
}

export interface RegisterRequest {
  profile: {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    password: string;
  };
  turnstileToken?: string;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  data?: {
    message: string;
    email: string;
    // Propiedades opcionales para mantener compatibilidad con flujo antiguo
    user?: {
      profile: {
        userid: string;
        username: string;
        firstname: string;
        lastname: string;
        email: string;
        phone: string;
        estado: string;
        createdAt: string;
      };
      role: {
        role: string;
        permissions: string[];
      };
    };
    accessToken?: string;
    refreshToken?: string;
    expiresIn?: string;
    refreshExpiresIn?: string;
  };
  timestamp: string;
}

export interface ActivateAccountResponse {
  success: boolean;
  message: string;
  data?: {
    message: string;
    email: string;
  };
  timestamp: string;
}

export interface ResendActivationRequest {
  email: string;
  turnstileToken?: string;
}

export interface ResendActivationResponse {
  success: boolean;
  message: string;
  data?: {
    message: string;
  };
  timestamp: string;
}

export interface RequestPasswordResetRequest {
  email: string;
  turnstileToken?: string;
}

export interface RequestPasswordResetResponse {
  success: boolean;
  message: string;
  data?: {
    message: string;
  };
  timestamp: string;
}

export interface ConfirmPasswordResetRequest {
  token: string;
  newPassword: string;
  turnstileToken?: string;
}

export interface ConfirmPasswordResetResponse {
  success: boolean;
  message: string;
  data?: {
    message: string;
  };
  timestamp: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
  data?: {
    message: string;
  };
  timestamp: string;
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  data?: {
    profile: {
      PK: string;
      SK: string;
      ItemType: string;
      userid: string;
      username: string;
      firstname: string;
      lastname: string;
      email: string;
      phone: string;
      GSI1PK: string;
      GSI1SK: string;
      createdAt: string;
      updatedAt: string;
    };
    role: {
      PK: string;
      SK: string;
      ItemType: string;
      role: string;
      createdAt: string;
      updatedAt: string;
    };
    facturacion?: {
      PK: string;
      SK: string;
      ItemType: string;
      nombre: string;
      apellido: string | null;
      direccion: string;
      telefono: string;
      email: string;
      identificacion: string;
      tipo_identificacion: {
        descripcion: string;
        codigo: string;
      };
      GSI2PK: string;
      GSI2SK: string;
      createdAt: string;
      updatedAt: string;
    };
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Signal para el estado del usuario
  private currentUserSignal = signal<User | null>(null);
  
  // Exponer como readonly
  currentUser = this.currentUserSignal.asReadonly();
  
  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;

  constructor(private http: HttpClient) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    // Cargar usuario desde localStorage al iniciar (solo en el navegador)
    if (this.isBrowser) {
      this.loadUserFromStorage();
    }
  }

  private loadUserFromStorage(): void {
    if (!this.isBrowser || API_CONFIG.cookie_http_only) return;
    
    const userJson = localStorage.getItem(API_CONFIG.storage.userKey);
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUserSignal.set(user);
      } catch (error) {
        console.error('Error al cargar usuario desde localStorage:', error);
        localStorage.removeItem(API_CONFIG.storage.userKey);
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.login}`,
      credentials,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    ).pipe(
      tap(response => {
        console.log('🔐 Login response completa:', response);
        if (response.success && response.data && this.isBrowser) {
          console.log('👤 Datos del usuario recibidos:', response.data.user);
          
          // Guardar tokens solo si no está usando cookies HTTP-only
          if (!API_CONFIG.cookie_http_only) {
            localStorage.setItem(API_CONFIG.storage.tokenKey, response.data.accessToken);
            localStorage.setItem(API_CONFIG.storage.refreshTokenKey, response.data.refreshToken);
          }
          
          // Mapear el usuario desde la estructura de la API
          const user: User = {
            userid: response.data.user.profile.userid,
            username: response.data.user.profile.username,
            firstname: response.data.user.profile.firstname,
            lastname: response.data.user.profile.lastname,
            email: response.data.user.profile.email,
            phone: response.data.user.profile.phone,
            role: response.data.user.role.role
          };
          
          console.log('✅ Usuario mapeado:', user);
          
          // Guardar usuario solo si no usa cookies HTTP-only
          if (!API_CONFIG.cookie_http_only) {
            localStorage.setItem(API_CONFIG.storage.userKey, JSON.stringify(user));
          }
          
          // Actualizar signal
          this.currentUserSignal.set(user);
          
          console.log('💾 Usuario guardado en localStorage y signal actualizado');
        }
      })
    );
  }

  checkEmail(request: CheckEmailRequest): Observable<CheckEmailResponse> {
    return this.http.post<CheckEmailResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.checkEmail}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  register(request: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.register}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    ).pipe(
      tap(response => {
        console.log('📝 Register response completa:', response);
        
        // Nuevo flujo: Solo mensaje de activación por email
        if (response.success && response.data && !response.data.user) {
          console.log('✉️ Email de activación enviado a:', response.data.email);
          console.log('📨 Mensaje:', response.data.message);
          return;
        }
        
        // Flujo antiguo (compatibilidad): Auto-login con tokens
        if (response.success && response.data && response.data.user && this.isBrowser) {
          console.log('👤 Usuario registrado (flujo antiguo con auto-login):', response.data.user);
          
          // Guardar tokens solo si no está usando cookies HTTP-only
          if (!API_CONFIG.cookie_http_only && response.data.accessToken && response.data.refreshToken) {
            localStorage.setItem(API_CONFIG.storage.tokenKey, response.data.accessToken);
            localStorage.setItem(API_CONFIG.storage.refreshTokenKey, response.data.refreshToken);
          }
          
          // Mapear el usuario desde la estructura de la API
          const user: User = {
            userid: response.data.user.profile.userid,
            username: response.data.user.profile.username,
            firstname: response.data.user.profile.firstname,
            lastname: response.data.user.profile.lastname,
            email: response.data.user.profile.email,
            phone: response.data.user.profile.phone,
            role: response.data.user.role.role
          };
          
          console.log('✅ Usuario mapeado:', user);
          
          // Guardar usuario solo si no usa cookies HTTP-only
          if (!API_CONFIG.cookie_http_only) {
            localStorage.setItem(API_CONFIG.storage.userKey, JSON.stringify(user));
          }
          
          // Actualizar signal
          this.currentUserSignal.set(user);
          
          console.log('💾 Usuario registrado y sesión iniciada automáticamente');
        }
      })
    );
  }

  activateAccount(token: string): Observable<ActivateAccountResponse> {
    return this.http.get<ActivateAccountResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.activate}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        },
        params: {
          token
        }
      }
    );
  }

  resendActivation(request: ResendActivationRequest): Observable<ResendActivationResponse> {
    return this.http.post<ResendActivationResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.resendActivation}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  requestPasswordReset(request: RequestPasswordResetRequest): Observable<RequestPasswordResetResponse> {
    return this.http.post<RequestPasswordResetResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.requestPasswordReset}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  confirmPasswordReset(request: ConfirmPasswordResetRequest): Observable<ConfirmPasswordResetResponse> {
    return this.http.post<ConfirmPasswordResetResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.confirmPasswordReset}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  changePassword(request: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.changePassword}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  getProfile(): Observable<ProfileResponse> {
    // El interceptor se encargará de agregar la autenticación
    return this.http.get<ProfileResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.profle}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    );
  }

  logout(): Observable<any> {
    if (!this.isBrowser) {
      return new Observable(observer => {
        observer.next({});
        observer.complete();
      });
    }
    
    const refreshToken = !API_CONFIG.cookie_http_only 
      ? localStorage.getItem(API_CONFIG.storage.refreshTokenKey) 
      : null;
    
    // El interceptor se encargará de agregar la autenticación
    return this.http.post(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.logout}`,
      API_CONFIG.cookie_http_only ? {} : { refreshToken },
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    ).pipe(
      tap(() => {
        this.clearSession();
      })
    );
  }

  clearSession(): void {
    if (!this.isBrowser) return;
    
    // Limpiar tokens
    localStorage.removeItem(API_CONFIG.storage.tokenKey);
    localStorage.removeItem(API_CONFIG.storage.refreshTokenKey);
    localStorage.removeItem(API_CONFIG.storage.userKey);
    
    // Limpiar signal
    this.currentUserSignal.set(null);
  }

  /**
   * Actualiza el usuario actual desde los datos del perfil
   * Útil cuando se valida la sesión con /auth/me en modo cookie_http_only
   */
  setCurrentUserFromProfile(profile: {
    userid: string;
    username: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
  }): void {
    if (!profile) return;
    
    const user: User = {
      userid: profile.userid,
      username: profile.username,
      firstname: profile.firstname,
      lastname: profile.lastname,
      email: profile.email,
      phone: profile.phone
    };
    
    this.currentUserSignal.set(user);
    
    // Guardar en localStorage solo si no está usando cookies HTTP-only
    if (this.isBrowser && !API_CONFIG.cookie_http_only) {
      localStorage.setItem(API_CONFIG.storage.userKey, JSON.stringify(user));
    }
  }

  isAuthenticated(): boolean {
    return this.currentUserSignal() !== null;
  }

  getAccessToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(API_CONFIG.storage.tokenKey);
  }

  getRefreshToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(API_CONFIG.storage.refreshTokenKey);
  }
}
